import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useState } from 'react';
import {
  LAMBDA_MEMORY_MB_INPUT_PROPS,
  LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS,
} from 'config-editor-common';
import { CONDOR_DEFAULT_LAMBDA_RUNTIME, type CondorLambdaDefaults } from 'config-editor-common';

const useStyles = makeStyles(theme => ({
  defaultsSection: {
    marginBottom: theme.spacing(3),
    '&:before': { display: 'none' },
  },
  defaultsSummary: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px dashed ${theme.palette.divider}`,
    minHeight: 48,
    '&.Mui-expanded': { minHeight: 48 },
  },
  defaultsDetails: {
    flexDirection: 'column',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderTop: `1px dashed ${theme.palette.divider}`,
  },
  layerChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  layerAddRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
    marginTop: theme.spacing(1),
  },
}));

interface CondorDefaultsEditorProps {
  defaults?: CondorLambdaDefaults;
  onChange: (defaults: CondorLambdaDefaults) => void;
}

export const CondorDefaultsEditor = ({
  defaults: propsDefaults,
  onChange,
}: CondorDefaultsEditorProps) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [layerInput, setLayerInput] = useState('');

  const defaults: CondorLambdaDefaults = {
    timeout: propsDefaults?.timeout ?? 24,
    memory_size: propsDefaults?.memory_size ?? 1024,
    runtime: propsDefaults?.runtime ?? CONDOR_DEFAULT_LAMBDA_RUNTIME,
    layer_names: Array.isArray(propsDefaults?.layer_names) ? [...propsDefaults.layer_names] : [],
  };

  const update = (field: keyof CondorLambdaDefaults, value: unknown) => {
    onChange({ ...defaults, [field]: value });
  };

  const layerNames = defaults.layer_names ?? [];

  const addLayer = () => {
    const v = layerInput.trim();
    if (!v) return;
    update('layer_names', [...layerNames, v]);
    setLayerInput('');
  };

  const removeLayer = (index: number) => {
    const next = [...layerNames];
    next.splice(index, 1);
    update('layer_names', next);
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      className={classes.defaultsSection}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.defaultsSummary}>
        <Typography variant="subtitle2">
          Endpoint Lambda Defaults
          {defaults.runtime ? ` (${defaults.runtime})` : ''}
        </Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.defaultsDetails}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Runtime"
              value={defaults.runtime ?? CONDOR_DEFAULT_LAMBDA_RUNTIME}
              onChange={e => update('runtime', e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              placeholder={CONDOR_DEFAULT_LAMBDA_RUNTIME}
              helperText="Default Lambda runtime for all endpoint lambdas"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary" display="block">
              Layer names
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              style={{ marginBottom: 4 }}
            >
              Layer names applied to all endpoint lambdas (optional)
            </Typography>
            {layerNames.length > 0 && (
              <Box className={classes.layerChips}>
                {layerNames.map((name, i) => (
                  <Chip
                    key={`${name}-${i}`}
                    label={name}
                    onDelete={() => removeLayer(i)}
                    size="small"
                  />
                ))}
              </Box>
            )}
            <Box className={classes.layerAddRow}>
              <TextField
                label="Add layer"
                value={layerInput}
                onChange={e => setLayerInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addLayer();
                  }
                }}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="e.g. python-httpx-layer, graphql-error-handler-training"
              />
              <Button variant="outlined" color="primary" onClick={addLayer}>
                Add
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Timeout (s)"
              type="number"
              value={defaults.timeout ?? 24}
              onChange={e => update('timeout', parseInt(e.target.value, 10) || 24)}
              fullWidth
              variant="outlined"
              size="small"
              inputProps={{ ...LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Memory (MB)"
              type="number"
              value={defaults.memory_size ?? 1024}
              onChange={e => update('memory_size', parseInt(e.target.value, 10) || 1024)}
              fullWidth
              variant="outlined"
              size="small"
              inputProps={{ ...LAMBDA_MEMORY_MB_INPUT_PROPS }}
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
